import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge, { PlatformTag } from "@/components/StatusBadge";
import { CalendarDays, Rocket, CheckCircle2, Filter } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = ["all", "instagram", "tiktok", "facebook", "linkedin"];
const STATUSES = ["all", "draft", "approved", "scheduled", "published", "failed"];

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get("/content");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const setItemStatus = async (id, s) => {
    try {
      if (s === "published") await api.post(`/content/${id}/publish`);
      else await api.put(`/content/${id}/status`, { status: s });
      setItems((its) => its.map((it) => (it.id === id ? { ...it, status: s } : it)));
      toast.success(`Marked ${s}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const filtered = useMemo(
    () =>
      items.filter(
        (it) =>
          (platform === "all" || it.platform === platform) &&
          (status === "all" || it.status === status)
      ),
    [items, platform, status]
  );

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((it) => {
      const k = it.scheduled_date;
      (g[k] = g[k] || []).push(it);
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <div className="fade-up flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <CalendarDays size={24} />
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold">Content Calendar</h1>
          <p className="text-muted-foreground">Review, approve, schedule and publish across all campaigns.</p>
        </div>
      </div>

      <Card className="border-border p-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Platform</span>
            <div className="flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => (
                <button key={p} onClick={() => setPlatform(p)} data-testid={`filter-platform-${p}`}
                  className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    platform === p ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</span>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(s)} data-testid={`filter-status-${s}`}
                  className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    status === s ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : grouped.length === 0 ? (
        <Card className="border-dashed p-12 text-center text-muted-foreground">
          No scheduled content yet. Generate a campaign to fill your calendar.{" "}
          <Link to="/campaigns/new" className="font-semibold text-primary hover:underline">Create one</Link>.
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([date, dayItems]) => (
            <div key={date}>
              <div className="mb-3 flex items-center gap-3">
                <h3 className="font-head text-lg font-bold">{fmtDate(date)}</h3>
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">{dayItems.length} item(s)</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {dayItems.map((it) => (
                  <Card key={it.id} className="flex flex-col border-border p-4 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`cal-item-${it.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <PlatformTag platform={it.platform} />
                      <StatusBadge status={it.status} />
                    </div>
                    <p className="mt-3 text-xs font-medium text-muted-foreground">{it.type}</p>
                    <p className="mt-1 line-clamp-2 font-semibold">{it.hook}</p>
                    <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{it.body}</p>
                    <div className="mt-4 flex gap-2">
                      {it.status === "draft" && (
                        <Button size="sm" onClick={() => setItemStatus(it.id, "approved")} data-testid={`cal-approve-${it.id}`}
                          className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                          <CheckCircle2 size={13} className="mr-1" /> Approve
                        </Button>
                      )}
                      {(it.status === "approved" || it.status === "scheduled") && (
                        <Button size="sm" onClick={() => setItemStatus(it.id, "published")} data-testid={`cal-publish-${it.id}`}
                          className="rounded-full bg-primary hover:bg-primary/90">
                          <Rocket size={13} className="mr-1" /> Publish
                        </Button>
                      )}
                      <Link to={`/campaigns/${it.campaign_id}`} className="ml-auto self-center text-xs text-muted-foreground hover:text-primary">
                        Edit →
                      </Link>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
